@if (booking) {
  <div class="">
    <!-- Main header with status dropdown -->
    <div class="flex h-16 items-center justify-between border-b border-gray-200 p-4">
      <div class="flex items-center">
        <i class="pi pi-calendar mr-3 text-xl text-primary"></i>
        <h5 class="m-0 text-lg font-medium text-gray-800 dark:text-gray-300">{{ 'booking.detail.generalInfo' | translate }}</h5>
      </div>

      <!-- Status selection moved to header -->
      <div class="w-80">
        <p-select
          [options]="bookingStatusItems"
          [(ngModel)]="selectedStatusId"
          [showClear]="false"
          optionLabel="localizableTitle"
          optionValue="value"
          [disabled]="loading"
          styleClass="w-full"
          (onChange)="onStatusChange()"
          inputId="booking-status"
          [ngClass]="{ 'border-primary': changeTracker.isFieldChanged('bookingStatus') }"
        >
          <ng-template
            pTemplate="selectedItem"
            let-item
          >
            <div class="flex h-[28px] w-full items-center">
              @if (getSelectedStatusBadge()) {
                <app-badge-info
                  [badge]="getSelectedStatusBadge()!"
                  class="w-full"
                ></app-badge-info>
              } @else {
                <span>{{ item?.localizableTitle | translate }}</span>
              }
            </div>
          </ng-template>
          <ng-template
            pTemplate="item"
            let-item
          >
            <div class="flex h-[36px] w-full items-center">
              @if (item?.model?.id && getStatusBadge(item.model.id)) {
                <app-badge-info
                  [badge]="getStatusBadge(item.model.id)!"
                  class="w-full"
                ></app-badge-info>
              } @else {
                <span>{{ item?.localizableTitle | translate }}</span>
              }
            </div>
          </ng-template>
        </p-select>
      </div>
    </div>

    <!-- Form content -->
    <div class="p-4">
      <!-- Stay Details - Connected group with visual relationship -->
      <div class="mb-7">
        <div class="mb-3 flex items-center">
          <i class="pi pi-calendar mr-2 text-primary"></i>
          <h6 class="text-md m-0 font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.stayDetails' | translate }}</h6>
        </div>
        <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
          <div class="grid">
            <!-- Stay Duration Group with Dates -->
            <div class="col-12 mb-4 flex flex-col gap-4 md:flex-row">
              <!-- Check-in date -->
              <div class="flex-1">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.checkIn' | translate }}</label>
                <p-datepicker
                  [(ngModel)]="booking.start_date"
                  [showIcon]="true"
                  [showButtonBar]="true"
                  dateFormat="yy-mm-dd"
                  [disabled]="loading"
                  styleClass="w-full calendar-clean"
                  inputId="check-in-date"
                  appendTo="body"
                  [inputStyleClass]="changeTracker.isFieldChanged('start_date') ? 'border-primary' : 'bg-white border-0'"
                  class="block w-full"
                  (onSelect)="onFieldChange('start_date', $event)"
                >
                </p-datepicker>
                <div class="mt-1 text-xs text-gray-500 dark:text-gray-300">{{ 'booking.detail.arrivalDate' | translate }}</div>
              </div>

              <!-- Check-out date -->
              <div class="flex-1">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.checkOut' | translate }}</label>
                <p-datepicker
                  [(ngModel)]="booking.end_date"
                  [showIcon]="true"
                  [showButtonBar]="true"
                  dateFormat="yy-mm-dd"
                  [disabled]="loading"
                  styleClass="w-full calendar-clean"
                  inputId="check-out-date"
                  [inputStyleClass]="changeTracker.isFieldChanged('end_date') ? 'border-primary' : 'bg-white border-0'"
                  appendTo="body"
                  class="block w-full"
                  (onSelect)="onFieldChange('end_date', $event)"
                >
                </p-datepicker>
                <div class="mt-1 text-xs text-gray-500 dark:text-gray-300">{{ 'booking.detail.departureDate' | translate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Occupant information section - Visual group for people/rooms -->
      <div class="mb-7">
        <div class="mb-3 flex items-center">
          <i class="pi pi-users mr-2 text-primary"></i>
          <h6 class="text-md m-0 font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.occupancy' | translate }}</h6>
        </div>

        <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
          <div class="flex flex-col gap-4 md:flex-row">
            <!-- Visual group for people (adults + children) -->
            <div class="flex-1 border-gray-200 pr-0 md:border-r md:pr-4">
              <div class="mb-3 text-sm text-gray-500 dark:text-gray-300">{{ 'booking.detail.people' | translate }}</div>

              <div class="grid">
                <!-- Adults input with icon -->
                <div class="col-12 md:col-6 mb-3">
                  <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.adults' | translate }}</label>
                  <div class="relative">
                    <div class="absolute left-0 top-0 flex h-full w-10 items-center justify-center text-gray-500">
                      <i class="pi pi-user"></i>
                    </div>
                    <p-inputNumber
                      [(ngModel)]="booking.adult_occupants"
                      [showButtons]="true"
                      [min]="1"
                      styleClass="w-full input-clean"
                      inputId="adult-count"
                      [inputStyleClass]="changeTracker.isFieldChanged('adult_occupants') ? 'border-primary pl-10' : 'bg-white border-0 pl-10'"
                      [disabled]="loading"
                      (onInput)="onFieldChange('adult_occupants', $event.value)"
                    >
                    </p-inputNumber>
                  </div>
                </div>

                <!-- Children input with icon -->
                <div class="col-12 md:col-6 mb-3">
                  <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.children' | translate }}</label>
                  <div class="relative">
                    <div class="absolute left-0 top-0 flex h-full w-10 items-center justify-center text-gray-500">
                      <i class="pi pi-users"></i>
                    </div>
                    <p-inputNumber
                      [(ngModel)]="booking.child_occupants"
                      [showButtons]="true"
                      [min]="0"
                      styleClass="w-full input-clean"
                      inputId="child-count"
                      [inputStyleClass]="changeTracker.isFieldChanged('child_occupants') ? 'border-primary pl-10' : 'bg-white border-0 pl-10'"
                      [disabled]="loading"
                      (onInput)="onFieldChange('child_occupants', $event.value)"
                    >
                    </p-inputNumber>
                  </div>
                </div>
              </div>

              <!-- Total occupants summary -->
              <div class="my-4 rounded-lg bg-gray-100 p-3 text-center text-sm dark:bg-black">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.totalOccupants' | translate }}</span>
                <span class="font-medium">{{ (booking.adult_occupants || 0) + (booking.child_occupants || 0) }}</span>
              </div>
            </div>

            <!-- Rooms section -->
            <div class="flex-1 pl-0 md:pl-4">
              <div class="mb-3 text-sm text-gray-500 dark:text-gray-300">{{ 'booking.detail.rooms' | translate }}</div>
              <div class="mb-3">
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{
                  'booking.detail.numberOfRooms' | translate
                }}</label>
                <div class="relative">
                  <div class="absolute left-0 top-0 flex h-full w-10 items-center justify-center text-gray-500">
                    <i class="pi pi-home"></i>
                  </div>
                  <p-inputNumber
                    [(ngModel)]="booking.number_of_rooms"
                    [min]="1"
                    [showButtons]="true"
                    styleClass="w-full input-clean"
                    inputId="room-count"
                    [inputStyleClass]="changeTracker.isFieldChanged('number_of_rooms') ? 'border-primary pl-10' : 'bg-white border-0 pl-10'"
                    [disabled]="loading"
                    (onInput)="onFieldChange('number_of_rooms', $event.value)"
                  >
                  </p-inputNumber>
                </div>
              </div>

              <!-- People per room calc -->
              <div class="my-4 rounded-lg bg-gray-100 p-3 text-center text-sm dark:bg-black">
                <span class="font-medium">{{ getTotalOccupants() / (booking.number_of_rooms || 1) | number: '1.0-1' }}</span>
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.peoplePerRoom' | translate }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Booking references section -->
      <div class="mb-7">
        <div class="mb-3 flex items-center">
          <i class="pi pi-id-card mr-2 text-primary"></i>
          <h6 class="text-md m-0 font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.references' | translate }}</h6>
        </div>

        <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
          <div class="grid">
            <!-- Booking code (read-only with system icon) -->
            <div class="col-12 md:col-4 mb-4">
              <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{
                'booking.detail.reservationCode' | translate
              }}</label>
              <div class="relative flex items-center">
                <input
                  pInputText
                  type="text"
                  id="reservation-code"
                  [value]="booking.reservation_code || ''"
                  readonly
                  class="w-full bg-white"
                  [disabled]="loading"
                />
                <div class="absolute right-3 top-1/2 -translate-y-1/2 transform rounded-full bg-gray-100 p-1">
                  <i class="pi pi-lock text-xs text-gray-500"></i>
                </div>
              </div>
              <div class="mt-1 text-xs text-gray-500 dark:text-gray-300">{{ 'booking.detail.systemGeneratedCode' | translate }}</div>
            </div>

            <!-- External booking code -->
            <div class="col-12 md:col-4 mb-4">
              <label class="required mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{
                'booking.detail.externalReservationCode' | translate
              }}</label>
              <div class="relative flex items-center">
                <input
                  pInputText
                  type="text"
                  id="foreign-reservation-code"
                  [(ngModel)]="booking.foreign_reservation_code"
                  class="w-full"
                  [ngClass]="{ 'border-primary': changeTracker.isFieldChanged('foreign_reservation_code') }"
                  placeholder="{{ 'booking.detail.externalCodePlaceholder' | translate }}"
                  [disabled]="loading"
                  (ngModelChange)="onFieldChange('foreign_reservation_code', $event)"
                />
              </div>
              <div class="mt-1 text-xs text-gray-500 dark:text-gray-300">{{ 'booking.detail.externalSystemReference' | translate }}</div>
            </div>

            <!-- Mobile app login token (read-only, copyable) -->
            <div class="col-12 md:col-4 mb-4">
              <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.loginToken.label' | translate }}</label>
              @if (booking.login_token) {
                <app-login-token
                  [token]="booking.login_token"
                  variant="field"
                ></app-login-token>
              } @else {
                <div class="flex h-[42px] items-center text-sm text-gray-400 dark:text-gray-500">{{ 'booking.loginToken.missing' | translate }}</div>
              }
              <div class="mt-1 text-xs text-gray-500 dark:text-gray-300">{{ 'booking.loginToken.hint' | translate }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notes section -->
      <div class="mb-4">
        <div class="mb-3 flex items-center">
          <i class="pi pi-comment mr-2 text-primary"></i>
          <h6 class="text-md m-0 font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.additionalInfo' | translate }}</h6>
        </div>

        <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'booking.detail.notes' | translate }}</label>
          <div class="relative">
            <textarea
              pInputTextarea
              id="notes"
              [(ngModel)]="booking.note"
              rows="5"
              class="w-full bg-white p-3"
              placeholder="{{ 'booking.detail.notesPlaceholder' | translate }}"
              [disabled]="loading"
              [ngClass]="{ 'border-primary': changeTracker.isFieldChanged('note') }"
              (ngModelChange)="onFieldChange('note', $event)"
            >
            </textarea>
            <div class="absolute bottom-3 right-3 text-xs text-gray-400">{{ booking.note?.length || 0 }} {{ 'common.characters' | translate }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
} @else {
  <div class="card shadow-sm">
    <div class="flex min-h-[300px] flex-col items-center justify-center p-5">
      <div class="mb-4 flex flex-col items-center">
        <i class="pi pi-spin pi-spinner mb-3 text-3xl text-primary"></i>
        <h6 class="m-0 text-lg font-medium text-gray-600 dark:text-gray-300">{{ 'common.loading' | translate }}</h6>
      </div>
      <p class="max-w-md text-center text-gray-500 dark:text-gray-300">{{ 'booking.detail.loadingMessage' | translate }}</p>
    </div>
  </div>
}
